import React, { useEffect, useState } from 'react'
import { useMutation, useQuery } from '@apollo/react-hooks'
import { Checkbox, Form, Input, Upload } from 'antd'
import type { UploadFile, UploadProps } from 'antd'
import { useFormik } from 'formik'
import * as Yup from 'yup'
import { useTranslation } from 'react-i18next'
import { AkaButton, AkaTypography } from 'components'
import PageLayout from 'modules/layout/containers/PageLayout'
import { useUser } from 'providers'
import { SUBMIT_SECURITY_INCIDENT } from 'modules/core/mutations'
import { REGISTRATION_REQUEST_CAPTCHA } from 'modules/core/queries'

const FormItem = Form.Item
const { TextArea } = Input
const INVALID_CAPTCHA_ERROR_CODE = 'INVALID_CAPTCHA'
const VALIDATION_ERROR_CODE = 'VALIDATION_ERROR'
const TEXT_FIELD_MAX_LENGTH = 10000

type SubmissionError = {
	graphQLErrors?: Array<{
		message?: string
		extensions?: { code?: string }
	}>
}

type HpmSecurityIncidentPageProps = {
	requiresCaptcha?: boolean
}

const HpmSecurityIncidentPage = ({
	requiresCaptcha = false
}: HpmSecurityIncidentPageProps) => {
	const [t] = useTranslation()
	const { user, isAuthenticated } = useUser()
	const requiresAnonymousCaptcha = requiresCaptcha && !isAuthenticated
	const [submitted, setSubmitted] = useState(false)
	const [fileList, setFileList] = useState<UploadFile[]>([])
	const [uploadErrors, setUploadErrors] = useState<string[]>([])
	const [submissionError, setSubmissionError] = useState('')
	const [captchaError, setCaptchaError] = useState('')
	const [captchaRefreshFailed, setCaptchaRefreshFailed] = useState(false)
	const [captchaRefreshing, setCaptchaRefreshing] = useState(false)
	const [submitIncident, { loading }] = useMutation(SUBMIT_SECURITY_INCIDENT)
	const {
		data: captchaData,
		loading: captchaLoading,
		refetch: refetchCaptcha
	} = useQuery(REGISTRATION_REQUEST_CAPTCHA, {
		skip: !requiresAnonymousCaptcha
	})
	const captchaChallenge = captchaData?.captchaChallenge
	const refreshCaptcha = async () => {
		setCaptchaRefreshing(true)
		try {
			await refetchCaptcha()
			setCaptchaRefreshFailed(false)
		} catch (_) {
			setCaptchaRefreshFailed(true)
			setCaptchaError(t('auth.registrationRequestPage.captchaNotLoaded'))
		} finally {
			setCaptchaRefreshing(false)
		}
	}

	const formik = useFormik({
		initialValues: {
			contactName: '',
			email: '',
			phone: '',
			otherContact: '',
			reportTypes: [] as string[],
			description: '',
			discoveredAt: '',
			measuresTaken: '',
			affectedInstances: '',
			hpmVersion: '',
			operatingEnvironment: '',
			additionalInformation: '',
			captchaValue: ''
		},
		validationSchema: Yup.object({
			contactName: Yup.string()
				.trim()
				.max(255, t('outreach.validation.maxLength', { count: 255 }))
				.required(t('outreach.validation.required')),
			email: Yup.string()
				.email(t('auth.validations.email'))
				.max(255, t('outreach.validation.maxLength', { count: 255 })),
			phone: Yup.string()
				.matches(/^$|^\+?[0-9]+$/, t('outreach.validation.phone'))
				.max(100, t('outreach.validation.maxLength', { count: 100 })),
			otherContact: Yup.string().max(
				TEXT_FIELD_MAX_LENGTH,
				t('outreach.validation.maxLength', { count: TEXT_FIELD_MAX_LENGTH })
			),
			reportTypes: Yup.array().min(1, t('outreach.validation.reportType')),
			description: Yup.string()
				.trim()
				.max(
					TEXT_FIELD_MAX_LENGTH,
					t('outreach.validation.maxLength', {
						count: TEXT_FIELD_MAX_LENGTH
					})
				)
				.required(t('outreach.validation.required')),
			discoveredAt: Yup.string()
				.required(t('outreach.validation.required'))
				.test(
					'not-future',
					t('outreach.validation.futureDate'),
					value => !value || new Date(value) <= new Date()
				),
			measuresTaken: Yup.string().max(
				TEXT_FIELD_MAX_LENGTH,
				t('outreach.validation.maxLength', { count: TEXT_FIELD_MAX_LENGTH })
			),
			affectedInstances: Yup.number()
				.integer(t('outreach.validation.positiveInteger'))
				.min(1, t('outreach.validation.positiveInteger'))
				.required(t('outreach.validation.required')),
			hpmVersion: Yup.string()
				.trim()
				.max(255, t('outreach.validation.maxLength', { count: 255 }))
				.required(t('outreach.validation.required')),
			operatingEnvironment: Yup.string()
				.trim()
				.max(
					TEXT_FIELD_MAX_LENGTH,
					t('outreach.validation.maxLength', {
						count: TEXT_FIELD_MAX_LENGTH
					})
				)
				.required(t('outreach.validation.required')),
			additionalInformation: Yup.string().max(
				TEXT_FIELD_MAX_LENGTH,
				t('outreach.validation.maxLength', { count: TEXT_FIELD_MAX_LENGTH })
			),
			captchaValue: requiresAnonymousCaptcha
				? Yup.string().required(
						t('auth.registrationRequestPage.captchaRequired')
					)
				: Yup.string()
		}),
		onSubmit: async ({ captchaValue, ...values }) => {
			setSubmissionError('')
			setCaptchaError('')
			if (
				requiresAnonymousCaptcha &&
				(captchaRefreshing ||
					captchaRefreshFailed ||
					!captchaChallenge?.hashKey)
			) {
				setCaptchaError(t('auth.registrationRequestPage.captchaNotLoaded'))
				return
			}

			try {
				await submitIncident({
					variables: {
						...values,
						discoveredAt: new Date(values.discoveredAt).toISOString(),
						affectedInstances: Number(values.affectedInstances),
						attachments: fileList
							.filter(file => !getUploadFileErrors([file]).length)
							.map(file => file.originFileObj)
							.filter(Boolean),
						captchaKey: requiresAnonymousCaptcha
							? captchaChallenge?.hashKey
							: undefined,
						captchaValue: requiresAnonymousCaptcha ? captchaValue : undefined
					}
				})
				setSubmitted(true)
			} catch (error) {
				setSubmitted(false)
				const graphQLError = (error as SubmissionError).graphQLErrors?.find(
					item => item.extensions?.code
				)
				const errorCode = graphQLError?.extensions?.code

				if (requiresAnonymousCaptcha) {
					formik.setFieldValue('captchaValue', '', false)
				}

				if (errorCode === INVALID_CAPTCHA_ERROR_CODE) {
					setCaptchaError(t('auth.registrationRequestPage.captchaInvalid'))
				} else if (
					errorCode === VALIDATION_ERROR_CODE &&
					graphQLError?.message
				) {
					setSubmissionError(graphQLError.message)
				} else {
					setSubmissionError(t('outreach.error'))
				}

				if (requiresAnonymousCaptcha) {
					await refreshCaptcha()
				}
			}
		}
	})

	useEffect(() => {
		if (!requiresCaptcha && user) {
			formik.setValues(values => ({
				...values,
				contactName: `${user.firstName || ''} ${user.lastName || ''}`.trim(),
				email: user.email || ''
			}))
		}
	}, [user]) // eslint-disable-line react-hooks/exhaustive-deps

	const handleFieldChange = (
		event: React.ChangeEvent<HTMLInputElement | HTMLTextAreaElement>
	) => {
		setSubmissionError('')
		formik.handleChange(event)
	}
	const handleCaptchaRefresh = () => {
		setCaptchaError('')
		formik.setFieldValue('captchaValue', '', false)
		refreshCaptcha()
	}

	const field = (
		name: keyof typeof formik.values,
		label: string,
		input: React.ReactNode
	) => (
		<FormItem
			label={label}
			validateStatus={
				formik.touched[name] && formik.errors[name] ? 'error' : ''
			}
			help={formik.touched[name] && formik.errors[name]}
		>
			{input}
		</FormItem>
	)

	const getUploadFileErrors = (files: UploadFile[]) => {
		const errors: string[] = []
		const allowedExtensions = ['pdf', 'png', 'jpg', 'jpeg', 'log']

		files.forEach(file => {
			const extension = file.name.split('.').pop()?.toLowerCase()
			const fileSize = file.originFileObj?.size ?? file.size ?? 0

			if (!extension || !allowedExtensions.includes(extension)) {
				errors.push(
					t('outreach.uploadErrors.invalidType', { fileName: file.name })
				)
			}
			if (fileSize > 10 * 1024 * 1024) {
				errors.push(
					t('outreach.uploadErrors.fileTooLarge', { fileName: file.name })
				)
			}
		})

		if (files.length > 10) {
			files.slice(10).forEach(file => {
				errors.push(
					t('outreach.uploadErrors.maxFiles', { fileName: file.name })
				)
			})
		}

		return errors
	}

	const uploadProps: UploadProps = {
		multiple: true,
		accept: '.pdf,.png,.jpg,.jpeg,.log',
		beforeUpload: () => false,
		fileList,
		onChange: ({ fileList: nextFileList }) => {
			setSubmissionError('')
			setFileList(nextFileList)
			setUploadErrors(getUploadFileErrors(nextFileList))
		},
		onRemove: file => {
			setSubmissionError('')
			const nextFileList = fileList.filter(item => item.uid !== file.uid)
			setFileList(nextFileList)
			setUploadErrors(getUploadFileErrors(nextFileList))
			return true
		}
	}

	const content = submitted ? (
		<div
			style={{
				maxWidth: 700,
				margin: '0 auto',
				padding: '32px 24px',
				textAlign: 'center'
			}}
		>
			<AkaTypography
				variant="h1"
				style={{ fontWeight: 'bold', margin: '32px auto' }}
			>
				{t('outreach.success.title')}
			</AkaTypography>
			<AkaTypography variant="bodySegoe">
				{t('outreach.success.message')
					.split('\n')
					.map(paragraph => (
						<p key={paragraph} style={{ margin: '0 0 10px' }}>
							{paragraph}
						</p>
					))}
			</AkaTypography>
		</div>
	) : (
		<div style={{ maxWidth: 700, margin: '0 auto', padding: '32px 24px' }}>
			<AkaTypography
				variant="h1"
				style={{ fontWeight: 'bold', margin: '32px auto' }}
			>
				{t('outreach.title')}
			</AkaTypography>
			<AkaTypography variant="bodySegoe">
				{t('outreach.introduction')
					.split('\n')
					.map(paragraph => (
						<p key={paragraph} style={{ width: '100%', marginBottom: 10 }}>
							{paragraph}
						</p>
					))}
			</AkaTypography>
			{submissionError && (
				<AkaTypography color="error">{submissionError}</AkaTypography>
			)}
			<Form
				style={{ fontFamily: 'SegoeUI, sans-serif' }}
				onFinish={formik.handleSubmit}
				layout="vertical"
			>
				{field(
					'contactName',
					t('outreach.fields.contactName'),
					<Input
						name="contactName"
						maxLength={255}
						value={formik.values.contactName}
						onChange={handleFieldChange}
						onBlur={formik.handleBlur}
					/>
				)}
				{field(
					'email',
					t('outreach.fields.email'),
					<Input
						name="email"
						type="email"
						maxLength={255}
						value={formik.values.email}
						onChange={handleFieldChange}
						onBlur={formik.handleBlur}
					/>
				)}
				{field(
					'phone',
					t('outreach.fields.phone'),
					<Input
						name="phone"
						inputMode="tel"
						maxLength={100}
						value={formik.values.phone}
						onChange={event => {
							setSubmissionError('')
							formik.setFieldValue(
								'phone',
								event.target.value
									.replace(/[^0-9+]/g, '')
									.replace(/(?!^)\+/g, '')
							)
						}}
						onBlur={formik.handleBlur}
					/>
				)}
				{field(
					'otherContact',
					t('outreach.fields.otherContact'),
					<TextArea
						name="otherContact"
						rows={3}
						maxLength={TEXT_FIELD_MAX_LENGTH}
						value={formik.values.otherContact}
						onChange={handleFieldChange}
						onBlur={formik.handleBlur}
					/>
				)}
				<FormItem
					label={t('outreach.fields.reportTypes')}
					validateStatus={
						formik.touched.reportTypes && formik.errors.reportTypes
							? 'error'
							: ''
					}
					help={formik.touched.reportTypes && formik.errors.reportTypes}
				>
					<Checkbox.Group
						options={[
							{
								label: t('outreach.reportTypes.vulnerability'),
								value: 'HPM_VULNERABILITY'
							},
							{
								label: t('outreach.reportTypes.incident'),
								value: 'HPM_SECURITY_INCIDENT'
							}
						]}
						value={formik.values.reportTypes}
						onChange={values => {
							setSubmissionError('')
							formik.setFieldValue('reportTypes', values)
						}}
					/>
				</FormItem>
				{field(
					'description',
					t('outreach.fields.description'),
					<TextArea
						name="description"
						rows={5}
						maxLength={TEXT_FIELD_MAX_LENGTH}
						value={formik.values.description}
						onChange={handleFieldChange}
						onBlur={formik.handleBlur}
					/>
				)}
				{field(
					'discoveredAt',
					t('outreach.fields.discoveredAt'),
					<Input
						name="discoveredAt"
						type="datetime-local"
						value={formik.values.discoveredAt}
						onChange={handleFieldChange}
						onBlur={formik.handleBlur}
					/>
				)}
				{field(
					'measuresTaken',
					t('outreach.fields.measuresTaken'),
					<TextArea
						name="measuresTaken"
						rows={3}
						maxLength={TEXT_FIELD_MAX_LENGTH}
						value={formik.values.measuresTaken}
						onChange={handleFieldChange}
						onBlur={formik.handleBlur}
					/>
				)}
				{field(
					'affectedInstances',
					t('outreach.fields.affectedInstances'),
					<Input
						name="affectedInstances"
						type="number"
						min={1}
						value={formik.values.affectedInstances}
						onChange={handleFieldChange}
						onBlur={formik.handleBlur}
					/>
				)}
				{field(
					'hpmVersion',
					t('outreach.fields.hpmVersion'),
					<Input
						name="hpmVersion"
						maxLength={255}
						value={formik.values.hpmVersion}
						onChange={handleFieldChange}
						onBlur={formik.handleBlur}
					/>
				)}
				<FormItem
					label={t('outreach.fields.operatingEnvironment')}
					extra={t('outreach.fields.operatingEnvironmentHint')}
					validateStatus={
						formik.touched.operatingEnvironment &&
						formik.errors.operatingEnvironment
							? 'error'
							: ''
					}
					help={
						formik.touched.operatingEnvironment &&
						formik.errors.operatingEnvironment
					}
				>
					<TextArea
						name="operatingEnvironment"
						rows={4}
						maxLength={TEXT_FIELD_MAX_LENGTH}
						value={formik.values.operatingEnvironment}
						onChange={handleFieldChange}
						onBlur={formik.handleBlur}
					/>
				</FormItem>
				{field(
					'additionalInformation',
					t('outreach.fields.additionalInformation'),
					<TextArea
						name="additionalInformation"
						rows={4}
						maxLength={TEXT_FIELD_MAX_LENGTH}
						value={formik.values.additionalInformation}
						onChange={handleFieldChange}
						onBlur={formik.handleBlur}
					/>
				)}
				{requiresAnonymousCaptcha && (
					<FormItem
						label={t('auth.registrationRequestPage.captchaLabel')}
						validateStatus={
							captchaError ||
							(formik.touched.captchaValue && formik.errors.captchaValue)
								? 'error'
								: ''
						}
						help={
							captchaError ||
							(formik.touched.captchaValue && formik.errors.captchaValue)
						}
					>
						{captchaLoading || captchaRefreshing ? (
							<AkaTypography variant="bodySegoe">
								{t('auth.registrationRequestPage.captchaLoading')}
							</AkaTypography>
						) : !captchaRefreshFailed && captchaChallenge?.imageUrl ? (
							<>
								<img
									src={captchaChallenge.imageUrl}
									alt={t('auth.registrationRequestPage.captchaImageAlt')}
									style={{ height: 80, border: '1px solid #d9d9d9' }}
								/>
								<Input
									name="captchaValue"
									value={formik.values.captchaValue}
									onChange={event => {
										setSubmissionError('')
										setCaptchaError('')
										formik.handleChange(event)
									}}
									onBlur={formik.handleBlur}
									placeholder={t(
										'auth.registrationRequestPage.captchaPlaceholder'
									)}
									style={{ marginTop: 8 }}
								/>
								<AkaButton
									type="link"
									onClick={handleCaptchaRefresh}
									disabled={captchaLoading || captchaRefreshing}
								>
									{t('auth.registrationRequestPage.captchaRefresh')}
								</AkaButton>
							</>
						) : (
							<>
								<AkaTypography variant="bodySegoe" color="error">
									{t('auth.registrationRequestPage.captchaNotLoaded')}
								</AkaTypography>
								<AkaButton type="link" onClick={handleCaptchaRefresh}>
									{t('auth.registrationRequestPage.captchaRefresh')}
								</AkaButton>
							</>
						)}
					</FormItem>
				)}
				<FormItem
					label={t('outreach.fields.attachments')}
					extra={t('outreach.fields.attachmentsHint')}
					help={
						uploadErrors.length > 0 ? (
							<div>
								{uploadErrors.map(errorMessage => (
									<div key={errorMessage}>{errorMessage}</div>
								))}
							</div>
						) : undefined
					}
					validateStatus={uploadErrors.length > 0 ? 'error' : ''}
				>
					<Upload {...uploadProps}>
						<AkaButton type="default">
							{t('outreach.fields.chooseFiles')}
						</AkaButton>
					</Upload>
				</FormItem>
				<FormItem>
					<AkaButton
						type="primary"
						htmlType="submit"
						loading={loading}
						disabled={
							uploadErrors.length > 0 ||
							(requiresAnonymousCaptcha &&
								(captchaLoading ||
									captchaRefreshing ||
									captchaRefreshFailed ||
									!captchaChallenge?.hashKey))
						}
					>
						{t('outreach.submit')}
					</AkaButton>
				</FormItem>
			</Form>
		</div>
	)

	return <PageLayout renderMainContent={content} />
}

export default HpmSecurityIncidentPage
